import * as THREE from 'three';
import type { Material, Mesh, Object3D } from 'three';
import type { Hitbox } from '../../types/spot-scene-response.gen';

const OBJECT_CLICK_ROLE = 'objectClick';
const HITBOX_VERSION = 1;
const disabledRaycast = () => {};

export type HitboxObjectRef = {
  id: string;
  name: string;
  type: 'Hitbox';
  spotarId: string;
};

function getSpotarHitboxConfig(userData: unknown): { role?: string } | null {
  if (!userData || typeof userData !== 'object') return null;
  const hitbox = (userData as { spotarHitbox?: unknown }).spotarHitbox;
  if (!hitbox || typeof hitbox !== 'object') return null;
  return hitbox as { role?: string };
}

function hasSpotarHitboxFlag(userData: unknown): boolean {
  return getSpotarHitboxConfig(userData) !== null;
}

function isObjectClickHitbox(object: Object3D): boolean {
  const config = getSpotarHitboxConfig(object.userData);
  return config?.role === OBJECT_CLICK_ROLE;
}

function hideHitboxMaterial(material: Material) {
  material.visible = false;
  material.transparent = true;
  material.opacity = 0;
  material.depthWrite = false;
  material.needsUpdate = true;
}

function isMeshObject(object: Object3D): object is Mesh {
  return (object as Mesh).isMesh === true;
}

/**
 * Direct child hitbox used as the editable On Click volume for a scene object.
 */
export function findObjectClickHitbox(host: Object3D): Object3D | null {
  for (const child of host.children) {
    if (isObjectClickHitbox(child)) return child;
  }
  return null;
}

/**
 * Depth-first: host itself, then children — first SpotAR hitbox in the subtree.
 * Matches outliner order under models like „Springkraut Copy“ → „Springkraut Hitbox“.
 */
export function findFirstHitboxInTree(host: Object3D): Object3D | null {
  if (hasSpotarHitboxFlag(host.userData)) return host;

  for (const child of host.children) {
    const hitbox = findFirstHitboxInTree(child);
    if (hitbox) return hitbox;
  }

  return null;
}

export function toHitboxObjectRef(hitbox: Object3D): HitboxObjectRef {
  const spotarId =
    typeof hitbox.userData?.spotarId === 'string' && hitbox.userData.spotarId.length > 0
      ? hitbox.userData.spotarId
      : hitbox.uuid;

  return {
    id: hitbox.uuid,
    name: hitbox.name || 'Hitbox',
    type: 'Hitbox',
    spotarId,
  };
}

function readVector3(values: number[], fallback: number): [number, number, number] {
  const readAxis = (index: number) =>
    Number.isFinite(values?.[index]) ? values[index] : fallback;
  return [readAxis(0), readAxis(1), readAxis(2)];
}

function createHitboxMesh(hitbox: Hitbox): Mesh {
  const [width, height, depth] = readVector3(hitbox.size, 1);
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(width, height, depth),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
  );

  mesh.uuid = hitbox.id;
  mesh.name = hitbox.name || 'Hitbox';
  mesh.position.set(...readVector3(hitbox.position, 0));
  mesh.rotation.set(...readVector3(hitbox.rotation, 0));
  mesh.scale.set(...readVector3(hitbox.scale, 1));
  mesh.userData.spotarId = hitbox.spotarId || hitbox.id;
  mesh.userData.spotarHitbox = hitbox.role
    ? { version: HITBOX_VERSION, role: hitbox.role }
    : { version: HITBOX_VERSION };

  return mesh;
}

/**
 * Rebuilds hitboxes that were authored in the editor under `host`.
 *
 * They are interaction volumes, not geometry, so they never become part of the
 * published GLB and arrive as plain box descriptions in the scene response.
 * The mesh keeps the editor uuid so graph object refs resolve against it.
 */
export function attachSceneHitboxes(host: Object3D, hitboxes: Hitbox[]) {
  for (const hitbox of hitboxes) {
    host.add(createHitboxMesh(hitbox));
  }
}

/** Makes SpotAR hitbox meshes invisible in the viewer. */
export function applyInvisibleHitboxes(root: Object3D) {
  root.traverse((child) => {
    if (!hasSpotarHitboxFlag(child.userData)) return;
    if (!isMeshObject(child)) return;

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) {
      if (!material) continue;
      hideHitboxMaterial(material);
    }
  });
}

/**
 * Decides which hitbox meshes the pointer raycast may hit.
 *
 * An object-click hitbox usually only shapes the On Click bounding box of its
 * host, so its invisible mesh must not swallow pointer hits. It becomes a click
 * target in its own right once an onHitboxClick node resolved to it — then the
 * raycast has to reach the mesh, otherwise that event could never fire.
 */
export function applyHitboxRaycastPolicy(root: Object3D, clickTargetIds: Set<string>) {
  root.traverse((child) => {
    if (!isMeshObject(child)) return;
    if (!isObjectClickHitbox(child)) return;
    if (clickTargetIds.has(child.uuid)) return;

    child.raycast = disabledRaycast;
  });
}
